<script setup lang="ts">
import { useBaseStore } from "@/stores/base";

const store = useBaseStore();

import { useApiClient } from "@/api/client";
import { onBeforeMount, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { toastApiResult } from "@/toaster";
import useToast from "@/ui/composables/useToast";

const { t } = useI18n();

const logout = async () => {
	const response = await useApiClient("users").logout();
	if (response.isOk()) {
		store.isAuthenticated = false;
		window.location.href = "/login";
	}
};

const showNotificationDot = ref(false);
const notificationTimer = ref(null);

const getUnreadNotificationCount = async () => {
	const response = await useApiClient("users").getUnreadNotificationCount();
	if (response.isOk()) {
		if (response.value.unread > 0) {
			showNotificationDot.value = true;
		} else {
			showNotificationDot.value = false;
		}

		const toasts = useToast();
		if (response.value.toasts.length > 0) {
			toasts.dismissAll("loading");
		}

		for (const toast of response.value.toasts) {
			toasts.info(toast.data.html_message, {
				id: toast.id,
				onDismiss: async () => {
					await useApiClient("users").markNotificationAsRead(toast.id);
				},
			});
		}
	}
};

onBeforeMount(() => {
	getUnreadNotificationCount();
	notificationTimer.value = setInterval(getUnreadNotificationCount, 30000);
});

onBeforeUnmount(() => {
	clearInterval(notificationTimer.value);
});

const stopImpersonating = async (uuid: string) => {
	const response = await toastApiResult(
		useApiClient("users").stopImpersonate(uuid)
	);
	if (response.isOk()) {
		window.location.href = "/users";
	}
};
</script>

<template>
	<!--	If the height is changed, also change the top of the header in HeaderBar.vue from the dashboard.-->
	<v-app-bar elevation="0" height="60" class="top-navigation-color">
		<div class="px-4">
			<div id="breadcrumbs-header"></div>
		</div>

		<v-spacer />

		<v-btn
			v-if="store.currentUser?.originalUser"
			@click="stopImpersonating(store.currentUser.uuid)"
			color="info"
			variant="tonal"
			class="mr-3"
		>
			<i class="fa-duotone fa-person-walking-dashed-line-arrow-right fa-lg"></i>
			<span class="pl-3">
				{{ t("stop_impersonating") }}
				{{ store.currentUser.name }}
			</span>
		</v-btn>

		<v-btn
			icon
			variant="text"
			color="on-surface"
			class="custom-hover-primary mr-2"
			to="/notifications"
		>
			<v-badge dot color="error" v-if="showNotificationDot">
				<i class="fa-regular fa-bell fa-lg"></i>
			</v-badge>
			<div v-else>
				<i class="fa-regular fa-bell fa-lg"></i>
			</div>
		</v-btn>

		<v-btn
			icon
			variant="text"
			color="on-surface"
			class="custom-hover-primary mr-2"
			:to="`/profile/${useBaseStore().currentUser.uuid}`"
		>
			<i class="fa-regular fa-user fa-lg" />
		</v-btn>

		<v-btn
			class="mr-2"
			icon
			color="on-surface"
			variant="text"
			@click.stop="logout"
		>
			<i class="fa-regular fa-right-from-bracket fa-lg" />
		</v-btn>
	</v-app-bar>
</template>
